// Centralized logging utility for Printboard
// Provides environment-aware logging with configurable levels

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

interface LoggerConfig {
  level: LogLevel
  enableConsole: boolean
  enableDebugFunctions: boolean
  enableEmojis: boolean
}

class Logger {
  private config: LoggerConfig
  private isDevelopment: boolean

  constructor() {
    // Check environment safely for both Node.js (Functions) and Browser (Vite)
    this.isDevelopment = false

    // Check Node.js environment (Netlify Functions)
    if (typeof process !== 'undefined' && process.env) {
      this.isDevelopment =
        process.env.NODE_ENV === 'development' ||
        process.env.NETLIFY_DEV === 'true'
    }
    // Browser environment (Vite) - check for dev server
    else if (typeof window !== 'undefined' && typeof location !== 'undefined') {
      // Detect development mode from hostname (localhost or netlify dev)
      this.isDevelopment =
        location.hostname === 'localhost' ||
        location.hostname === '127.0.0.1' ||
        location.port === '8888' ||
        location.port === '5173'
    }

    // Configure logging based on environment
    this.config = {
      level: this.isDevelopment ? LogLevel.DEBUG : LogLevel.ERROR,
      enableConsole: true,
      enableDebugFunctions: this.isDevelopment,
      enableEmojis: false, // Disable emojis to reduce noise
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enableConsole && level <= this.config.level
  }

  private formatMessage(
    component: string,
    message: string,
    emoji?: string
  ): string {
    const prefix = `[${component}]`
    const emojiPrefix = this.config.enableEmojis && emoji ? `${emoji} ` : ''
    return `${prefix} ${emojiPrefix}${message}`
  }

  error(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      // eslint-disable-next-line no-console
      console.error(this.formatMessage(component, message, '❌'), ...args)
    }
  }

  warn(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      // eslint-disable-next-line no-console
      console.warn(this.formatMessage(component, message, '⚠️'), ...args)
    }
  }

  info(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console
      console.info(this.formatMessage(component, message, 'ℹ️'), ...args)
    }
  }

  debug(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(component, message, '🔍'), ...args)
    }
  }

  // Specific methods for common operations
  stateChange(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(component, message, '🔄'), ...args)
    }
  }

  apiCall(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(component, message, '📤'), ...args)
    }
  }

  save(component: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage(component, message, '💾'), ...args)
    }
  }

  // Debug functions should only be available in development
  enableDebugFunctions(): boolean {
    return this.config.enableDebugFunctions
  }

  // Allow runtime configuration
  setLevel(level: LogLevel): void {
    this.config.level = level
  }

  setEmojis(enabled: boolean): void {
    this.config.enableEmojis = enabled
  }
}

// Export singleton instance
export const logger = new Logger()

// Export convenience functions
export const logError = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.error(component, message, ...args)

export const logWarn = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.warn(component, message, ...args)

export const logInfo = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.info(component, message, ...args)

export const logDebug = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.debug(component, message, ...args)

export const logStateChange = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.stateChange(component, message, ...args)

export const logApiCall = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.apiCall(component, message, ...args)

export const logSave = (
  component: string,
  message: string,
  ...args: unknown[]
) => logger.save(component, message, ...args)
